import { useEffect, useState } from 'react';

import useAxiosInstance from '../../axios/useAxiosInstance';
import { objectToQueryString } from '../../utils/queryParamsHelpers';
import { API_RESPONSE_STATUS_CODE, API_STATUS } from '../../consts/constants';
import { GENERIC_GET_API_FAILED_ERROR_MESSAGE } from '../../consts/constants';

/** 
 * 1. useFetch will initiate the API call on component mount automatically so that you don't require to use the useEffect hook in your component explicitly just to call the API.
 * 
 * 2. You can anytime omit this behaviour by passing skipApiCallOnMount: true inside the otherOptions object.
 * 
 * 3. "apiOptions" object is added so that you can pass custom data to the axios get method. For ex- headers.
 * 
 * Example to use this hook
 *  const { data } = useFetch({
    url: API_ENDPOINT,
    apiOptions: {
      headers: {
        sampleHeader: "value",
      },
    },
    otherOptions: {
      skipApiCallOnMount: true,
    },
  });
 * */

interface OtherOptions {
  skipApiCallOnMount?: boolean;
  isForNonLoggedInUser?: boolean;
  apiVersion?: string;
  apiModule?: string;
  apiBaseUrl?: string;
}

interface UseFetchParamsInterface<T> {
  url: string;
  apiOptions?: Object;
  otherOptions?: OtherOptions;
}

interface FetchDataInterface {
  overrideUrl?: string;
  queryParamsObject?: Object;
  stateUpdateNotRequired?: boolean;
  headers?: Record<string, string | number>;
  isTribyte?: boolean;
}

const useFetch = <T>({
  url,
  apiOptions = {},
  otherOptions = {
    skipApiCallOnMount: false,
    isForNonLoggedInUser: false,
  },
}: UseFetchParamsInterface<T>) => {
  const [data, setData] = useState<T | null>(null);
  const [apiStatus, setApiStatus] = useState<string>(API_STATUS.IDLE);
  const [error, setError] = useState<string | null>(null);

  const { apiVersion, skipApiCallOnMount, isForNonLoggedInUser, apiModule, apiBaseUrl } =
    otherOptions || {};

  const { axiosInstance } = useAxiosInstance({
    omitToken: isForNonLoggedInUser,
    apiVersion,
    apiModule,
    apiBaseUrl,
  });

  const fetchData = async (fetchDataParams?: FetchDataInterface) => {
    try {
      const { overrideUrl, queryParamsObject, stateUpdateNotRequired, headers: requestHeaders, isTribyte } =
        fetchDataParams || {};
      let modifiedURL = overrideUrl || url; // Use overrideUrl if provided, otherwise use the default url
      if (queryParamsObject && objectToQueryString(queryParamsObject)) {
        modifiedURL = `${modifiedURL}?${objectToQueryString(
          queryParamsObject,
        )}`;
      }
      !stateUpdateNotRequired && setApiStatus(API_STATUS.LOADING);
      !stateUpdateNotRequired && error && setError(null);
      const mergedOptions = requestHeaders
        ? { ...apiOptions, headers: { ...(apiOptions as any)?.headers, ...requestHeaders } }
        : apiOptions;
      const res = await axiosInstance.get(modifiedURL, mergedOptions);
      console.log(res);
      if (res?.data?.status === API_RESPONSE_STATUS_CODE.SUCCESS || (isTribyte && (res?.data?.status_code === 200 || res.status === 200))) {
        !stateUpdateNotRequired && setApiStatus(API_STATUS.SUCCESS);
        !stateUpdateNotRequired && setData(res?.data);
        return res?.data;
      }
      !stateUpdateNotRequired && setApiStatus(API_STATUS.ERROR);
      !stateUpdateNotRequired &&
        setError(res?.data?.message || GENERIC_GET_API_FAILED_ERROR_MESSAGE);
    } catch (err: any) {
      !fetchDataParams?.stateUpdateNotRequired &&
        setApiStatus(API_STATUS.ERROR);
      !fetchDataParams?.stateUpdateNotRequired &&
        setError(
          err?.response?.data?.message || GENERIC_GET_API_FAILED_ERROR_MESSAGE,
        );
    }
  };

  useEffect(() => {
    if (skipApiCallOnMount) {
      return;
    }
    fetchData();
  }, []);

  useEffect(() => {
    return () => {
      setData(null);
      setApiStatus(API_STATUS.IDLE);
      setError(null);
    };
  }, []);

  const isLoading = apiStatus === API_STATUS.LOADING;
  const isSuccess = apiStatus === API_STATUS.SUCCESS;
  const isError = apiStatus === API_STATUS.ERROR;

  return {
    apiStatus,
    data,
    error,
    fetchData,
    isError,
    isLoading,
    isSuccess,
    setError,
    setData,
  };
};

export default useFetch;
